import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserData } from 'app/shared/service/UserService';
import { CryptoService } from 'app/shared/utils/crypto.service';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TM_FALLBACK_EMAIL, tmEmailOrFallback } from './training-material.service';

// localStorage key written by app.component when /userDetails resolves at
// bootstrap. Read here as a session-wide cache so we don't re-fetch on every
// notification call site.
const CURRENT_USER_EMAIL_KEY = 'tmCurrentUserEmail';

export function setCurrentUserEmailCache(email: string | null | undefined): void {
  try {
    localStorage.setItem(CURRENT_USER_EMAIL_KEY, (email || '').trim());
  } catch {
    // localStorage may be unavailable (e.g. private mode) — silently degrade
    // to fallback resolution downstream.
  }
}

export function getCurrentUserEmail(): string {
  try {
    return tmEmailOrFallback(localStorage.getItem(CURRENT_USER_EMAIL_KEY));
  } catch {
    return TM_FALLBACK_EMAIL;
  }
}

// Per-file detail block used when consolidating multiple files into a single
// email (e.g. one approver approves N files for the same requester — one
// email with N file blocks instead of N emails).
export interface RequestEmailFile {
  fileName: string;
  fileType: string;
  requestedDate: string;
  linkedApp: string;
  practiceSet: string;
  // Optional split for apps that are no longer active (or have been removed
  // from the org). Rendered as separate rows in the email so the approver/
  // requester can tell the linkage still exists on the record but is no
  // longer manageable from the UI. Empty strings are hidden by the renderer.
  inactiveLinkedApp?: string;
  inactivePracticeSet?: string;
}

// Context payload that every template builder consumes. All notification call
// sites build one of these from the row + current user, hand it to the
// service, and the service handles template rendering + transport.
//
// Single-file callers populate the top-level fileName/fileType/... fields.
// Multi-file callers leave those blank and populate `files[]` instead — when
// `files.length > 1` the renderer emits one detail card per file.
export interface RequestEmailContext {
  fileName: string;
  fileType: string;
  requesterName: string;
  requestedDate: string;
  linkedApp: string;
  practiceSet: string;
  // Same split as RequestEmailFile — used by single-file callers. Empty
  // strings are hidden by the renderer.
  inactiveLinkedApp?: string;
  inactivePracticeSet?: string;
  requesterEmail?: string;
  approverName?: string;
  approverEmail?: string;
  rejectionReason?: string;
  files?: RequestEmailFile[];
}

interface EmailPayload {
  to: string;
  subject: string;
  body: string;       // plain-text rendering for the `body` field
  bodyHtml: string;   // HTML rendering for the `body_html` field
}

// Wire-format payload for POST /api/emails/send.
interface EmailServiceRequest {
  app_id: string;
  to: string[];
  subject: string;
  body: string;
  body_html: string;
  cc?: string[];
  bcc?: string[];
  correlation_id?: string;
  user_id?: string;
}

// Status drives the colour of the banner at the top of the HTML email and
// the "[Status]" label embedded in the subject (visual cue at the inbox
// level even before the email is opened).
type EmailStatus = 'submitted' | 'approved' | 'rejected' | 'cancelled';

interface TemplateParts {
  greetingName: string;     // "Hi {name},"
  intro: string;            // first paragraph after greeting
  outro: string;            // closing paragraph before signoff
  statusLabel: string;      // banner text in HTML, e.g. "Request submitted"
  status: EmailStatus;
  includeReason?: boolean;  // surface ctx.rejectionReason if present
}

const STATUS_COLOURS: Record<EmailStatus, { bg: string; text: string }> = {
  submitted: { bg: '#3182ce', text: '#ffffff' },
  approved:  { bg: '#38a169', text: '#ffffff' },
  rejected:  { bg: '#e53e3e', text: '#ffffff' },
  cancelled: { bg: '#718096', text: '#ffffff' }
};

// All toaster strings the workflow uses. Centralised here so the spec and the
// component code can't drift out of sync.
export const TM_TOASTER = {
  uploadSubmitted: 'Your request has been successfully submitted to the approver.',
  uploadCancelled: 'Your request has been successfully cancelled.',
  uploadApprovedSingle: 'The Request assigned to you has been approved successfully.',
  uploadRejectedSingle: 'The Request assigned to you has been rejected.',
  deletionSubmitted: 'Your request has been successfully submitted to the approver.',
  deletionApprovedSingle: 'The request has been successfully approved.',
  deletionRejectedSingle: 'The Request assigned to you has been rejected.'
};

@Injectable({ providedIn: 'root' })
export class EmailNotificationService {
  private readonly productName = 'StrataGem';

  constructor(private http: HttpClient, private crypto: CryptoService) { }

  // ── Upload flow ───────────────────────────────────────────────────────
  notifyUploadSubmittedToRequester(ctx: RequestEmailContext): Observable<boolean> {
    const n = this.fileCount(ctx);
    return this.dispatch(
      'UploadSubmitted→Requester',
      ctx,
      ctx.requesterEmail || ctx.requesterName,
      n > 1
        ? `${n} of your training material requests have been submitted for approval`
        : 'Your training material request has been submitted for approval',
      {
        greetingName: ctx.requesterName,
        statusLabel: n > 1 ? `${n} requests submitted` : 'Request submitted',
        status: 'submitted',
        intro: n > 1
          ? `${n} training material requests have been successfully submitted to the approver(s).`
          : 'Your training material request has been successfully submitted to the approver.',
        outro: "You'll receive another email once the approver has reviewed your request."
      }
    );
  }

  notifyUploadSubmittedToApprover(ctx: RequestEmailContext): Observable<boolean> {
    const n = this.fileCount(ctx);
    return this.dispatch(
      'UploadSubmitted→Approver',
      ctx,
      ctx.approverEmail || ctx.approverName || '',
      n > 1
        ? `${n} new training material approval requests assigned to you`
        : 'New training material approval request assigned to you',
      {
        greetingName: ctx.approverName || 'Approver',
        statusLabel: n > 1 ? `${n} approval requests assigned` : 'Approval request assigned',
        status: 'submitted',
        intro: n > 1
          ? `${n} new training material requests have been assigned to you for approval.`
          : 'A new training material request has been assigned to you for approval.',
        outro: 'Please review and approve or reject these requests in the Pending queue.'
      }
    );
  }

  notifyUploadCancelledToApprover(ctx: RequestEmailContext): Observable<boolean> {
    return this.dispatch(
      'UploadCancelled→Approver',
      ctx,
      ctx.approverEmail || ctx.approverName || '',
      'Training material request has been cancelled by requester',
      {
        greetingName: ctx.approverName || 'Approver',
        statusLabel: 'Request cancelled',
        status: 'cancelled',
        intro: 'A pending training material request assigned to you has been cancelled by the requester.',
        outro: 'No further action is required.'
      }
    );
  }

  notifyUploadApprovedToRequester(ctx: RequestEmailContext): Observable<boolean> {
    const n = this.fileCount(ctx);
    return this.dispatch(
      'UploadApproved→Requester',
      ctx,
      ctx.requesterEmail || ctx.requesterName,
      n > 1
        ? `${n} of your training material requests have been approved`
        : 'Your training material request has been approved',
      {
        greetingName: ctx.requesterName,
        statusLabel: n > 1 ? `${n} requests approved` : 'Request approved',
        status: 'approved',
        intro: n > 1
          ? `Good news! ${n} of your training material requests have been approved.`
          : 'Good news! Your training material request has been approved.',
        outro: 'Your content will now be available according to the configured publishing process.'
      }
    );
  }

  notifyUploadRejectedToRequester(ctx: RequestEmailContext): Observable<boolean> {
    const n = this.fileCount(ctx);
    return this.dispatch(
      'UploadRejected→Requester',
      ctx,
      ctx.requesterEmail || ctx.requesterName,
      n > 1
        ? `${n} of your training material requests have been rejected`
        : 'Your training material request has been rejected',
      {
        greetingName: ctx.requesterName,
        statusLabel: n > 1 ? `${n} requests rejected` : 'Request rejected',
        status: 'rejected',
        intro: n > 1
          ? `${n} of your training material requests have been rejected by the approver.`
          : 'Your training material request has been rejected by the approver.',
        outro: 'If you have questions, please contact your approver or support.',
        includeReason: true
      }
    );
  }

  // ── Deletion flow ─────────────────────────────────────────────────────
  notifyDeletionSubmittedToRequester(ctx: RequestEmailContext): Observable<boolean> {
    return this.dispatch(
      'DeletionSubmitted→Requester',
      ctx,
      ctx.requesterEmail || ctx.requesterName,
      'Your deletion request has been submitted for approval',
      {
        greetingName: ctx.requesterName,
        statusLabel: 'Deletion request submitted',
        status: 'submitted',
        intro: 'Your deletion request for published training material has been successfully submitted to the approver.',
        outro: "You'll receive another email after the approver reviews this request."
      }
    );
  }

  notifyDeletionSubmittedToApprover(ctx: RequestEmailContext): Observable<boolean> {
    return this.dispatch(
      'DeletionSubmitted→Approver',
      ctx,
      ctx.approverEmail || ctx.approverName || '',
      'New training material deletion request assigned to you',
      {
        greetingName: ctx.approverName || 'Approver',
        statusLabel: 'Deletion request assigned',
        status: 'submitted',
        intro: 'A new deletion request for published training material has been assigned to you for approval.',
        outro: 'Please review and approve or reject this request in the Pending queue.'
      }
    );
  }

  notifyDeletionApprovedToRequester(ctx: RequestEmailContext): Observable<boolean> {
    const n = this.fileCount(ctx);
    return this.dispatch(
      'DeletionApproved→Requester',
      ctx,
      ctx.requesterEmail || ctx.requesterName,
      n > 1
        ? `${n} of your training material deletion requests have been approved`
        : 'Your training material deletion request has been approved',
      {
        greetingName: ctx.requesterName,
        statusLabel: n > 1 ? `${n} deletions approved` : 'Deletion approved',
        status: 'approved',
        intro: n > 1
          ? `${n} of your deletion requests for training material have been approved.`
          : 'Your deletion request for the following training material has been approved.',
        outro: 'The content will be removed according to the configured deletion process.'
      }
    );
  }

  // PDF only ships approval for the deletion path; mirroring rejection here
  // keeps the workflow symmetric — without it, a rejected deletion would
  // silently leave the requester un-notified.
  notifyDeletionRejectedToRequester(ctx: RequestEmailContext): Observable<boolean> {
    const n = this.fileCount(ctx);
    return this.dispatch(
      'DeletionRejected→Requester',
      ctx,
      ctx.requesterEmail || ctx.requesterName,
      n > 1
        ? `${n} of your training material deletion requests have been rejected`
        : 'Your training material deletion request has been rejected',
      {
        greetingName: ctx.requesterName,
        statusLabel: n > 1 ? `${n} deletions rejected` : 'Deletion rejected',
        status: 'rejected',
        intro: n > 1
          ? `${n} of your deletion requests for training material have been rejected by the approver.`
          : 'Your deletion request for the following training material has been rejected by the approver.',
        outro: 'The content will remain published. If you have questions, please contact your approver or support.',
        includeReason: true
      }
    );
  }

  // Same reason as above — the upload flow has a cancel-to-approver template
  // (#3); the deletion flow needs the same when a requester withdraws.
  notifyDeletionCancelledToApprover(ctx: RequestEmailContext): Observable<boolean> {
    return this.dispatch(
      'DeletionCancelled→Approver',
      ctx,
      ctx.approverEmail || ctx.approverName || '',
      'Training material deletion request has been cancelled by requester',
      {
        greetingName: ctx.approverName || 'Approver',
        statusLabel: 'Deletion request cancelled',
        status: 'cancelled',
        intro: 'A pending deletion request assigned to you has been cancelled by the requester.',
        outro: 'No further action is required.'
      }
    );
  }

  // ── Composition ───────────────────────────────────────────────────────
  // Fans the parts out into both a plain-text and an HTML rendering and
  // hands the result to the transport layer. Keeps every notify* method a
  // pure declaration of "what" — the "how" of rendering lives here.
  private dispatch(
    processName: string,
    ctx: RequestEmailContext,
    to: string,
    subject: string,
    parts: TemplateParts
  ): Observable<boolean> {
    console.log(`[EmailNotificationService] [${processName}] dispatch`, {
      to,
      subject,
      fileName: ctx.fileName,
      requesterEmail: ctx.requesterEmail,
      approverEmail: ctx.approverEmail
    });
    return this.sendEmail(processName, {
      to,
      subject,
      body: this.renderText(parts, ctx),
      bodyHtml: this.renderHtml(parts, ctx)
    });
  }

  private fileCount(ctx: RequestEmailContext): number {
    return ctx.files && ctx.files.length > 0 ? ctx.files.length : 1;
  }

  // Coerces single-file contexts into the multi-file shape so renderers can
  // treat both paths uniformly.
  private resolveFiles(ctx: RequestEmailContext): RequestEmailFile[] {
    if (ctx.files && ctx.files.length > 0) return ctx.files;
    return [{
      fileName: ctx.fileName,
      fileType: ctx.fileType,
      requestedDate: ctx.requestedDate,
      linkedApp: ctx.linkedApp,
      practiceSet: ctx.practiceSet,
      inactiveLinkedApp: ctx.inactiveLinkedApp,
      inactivePracticeSet: ctx.inactivePracticeSet
    }];
  }

  private fileRows(file: RequestEmailFile, ctx: RequestEmailContext, includeRequester: boolean): { label: string; value: string }[] {
    const rows: { label: string; value: string }[] = [
      { label: 'File name', value: file.fileName || '-' },
      { label: 'File type', value: file.fileType || '-' }
    ];
    if (includeRequester) {
      rows.push({ label: 'Requested by', value: ctx.requesterName || '-' });
    }
    rows.push(
      { label: 'Requested date', value: file.requestedDate || '-' },
      { label: 'Linked app', value: file.linkedApp || '-' },
      { label: 'Practice sets', value: file.practiceSet || '-' }
    );
    // Email is the only surface where inactive linkages are named. Rows are
    // appended only when the source has values, so the common case (no
    // inactive links) renders identically to before.
    if (file.inactiveLinkedApp) {
      rows.push({ label: 'Inactive apps', value: file.inactiveLinkedApp });
    }
    if (file.inactivePracticeSet) {
      rows.push({ label: 'Inactive practice sets', value: file.inactivePracticeSet });
    }
    return rows;
  }

  private renderText(parts: TemplateParts, ctx: RequestEmailContext): string {
    const reasonLine = parts.includeReason && ctx.rejectionReason
      ? `\nRejection reason: ${ctx.rejectionReason}\n`
      : '';
    const files = this.resolveFiles(ctx);
    const isMulti = files.length > 1;
    const detailsBlock = isMulti
      ? files.map((f, i) => {
          const rows = this.fileRows(f, ctx, false)
            .map(r => `  - ${r.label}: ${r.value}`)
            .join('\n');
          return `File ${i + 1} of ${files.length}:\n${rows}`;
        }).join('\n\n')
      : this.fileRows(files[0], ctx, true)
          .map(r => `- ${r.label}: ${r.value}`)
          .join('\n');
    const requesterLine = isMulti && ctx.requesterName ? `Requested by: ${ctx.requesterName}\n\n` : '';
    return `Hi ${parts.greetingName},

${parts.intro}

${requesterLine}${isMulti ? 'Request details — ' + files.length + ' files:' : 'Request details:'}
${detailsBlock}
${reasonLine}
${parts.outro}

Thanks,
${this.productName} Team`;
  }

  private renderHtml(parts: TemplateParts, ctx: RequestEmailContext): string {
    const colour = STATUS_COLOURS[parts.status];
    // Email clients won't resolve relative asset paths — resolve against the
    // document's base URI so we pick up the deploy subpath (e.g. `/stratagem/`
    // on the server, `/` locally) from the <base href> tag automatically.
    const logoUrl = new URL('assets/img/stratagem.png', document.baseURI).href;
    const files = this.resolveFiles(ctx);
    const isMulti = files.length > 1;

    const buildRowsHtml = (rows: { label: string; value: string }[]) => rows.map(r => `
            <tr>
              <td style="padding:8px 0;color:#718096;font-size:13px;width:38%;vertical-align:top;">${this.escapeHtml(r.label)}</td>
              <td style="padding:8px 0;color:#1a202c;font-size:13px;font-weight:500;word-break:break-word;">${this.escapeHtml(r.value)}</td>
            </tr>`).join('');

    const requesterBanner = isMulti && ctx.requesterName ? `
              <p style="margin:0 0 14px;color:#4a5568;font-size:13px;">
                <span style="color:#718096;">Requested by:</span>
                <strong style="color:#1a202c;">${this.escapeHtml(ctx.requesterName)}</strong>
              </p>` : '';

    const detailsBlock = isMulti
      ? files.map((f, i) => `
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f7fafc;border:1px solid #e2e8f0;border-radius:6px;margin:0 0 12px;">
                <tr>
                  <td style="padding:10px 16px;background-color:#edf2f7;border-bottom:1px solid #e2e8f0;font-size:12px;font-weight:600;color:#4a5568;letter-spacing:0.5px;text-transform:uppercase;display:flex;align-items:center;justify-content:space-between;">
                    <span>File ${i + 1} of ${files.length}</span>
                    <span style="float:right;color:#718096;font-weight:500;text-transform:none;letter-spacing:0;">${this.escapeHtml(f.fileType || '')}</span>
                  </td>
                </tr>
                <tr>
                  <td style="padding:8px 16px 12px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%">
                      ${buildRowsHtml(this.fileRows(f, ctx, false))}
                    </table>
                  </td>
                </tr>
              </table>`).join('')
      : `
              <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f7fafc;border:1px solid #e2e8f0;border-radius:6px;margin:0 0 18px;">
                <tr>
                  <td style="padding:10px 16px;background-color:#edf2f7;border-bottom:1px solid #e2e8f0;font-size:12px;font-weight:600;color:#4a5568;letter-spacing:0.5px;text-transform:uppercase;">
                    Request details
                  </td>
                </tr>
                <tr>
                  <td style="padding:8px 16px 12px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%">
                      ${buildRowsHtml(this.fileRows(files[0], ctx, true))}
                    </table>
                  </td>
                </tr>
              </table>`;

    const detailsHeader = isMulti ? `
              <p style="margin:0 0 10px;font-size:12px;font-weight:600;color:#4a5568;letter-spacing:0.5px;text-transform:uppercase;">
                Request details — ${files.length} files
              </p>` : '';

    const reasonBlock = parts.includeReason && ctx.rejectionReason
      ? `
        <div style="margin:0 0 16px;padding:12px 16px;background:#fff5f5;border:1px solid #fed7d7;border-radius:6px;color:#742a2a;font-size:13px;">
          <strong style="display:block;margin-bottom:4px;">Rejection reason</strong>
          ${this.escapeHtml(ctx.rejectionReason)}
        </div>`
      : '';

    // Email-safe HTML: nested tables, inline styles only, no <head>/<style>.
    // Most email clients (Gmail / Outlook web / mobile) strip <style> blocks
    // and disregard external CSS, so every visual is set inline.
    return `<!DOCTYPE html>
<html>
<body style="margin:0;padding:0;background-color:#f4f6f8;">
  <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#f4f6f8;font-family:Arial,Helvetica,sans-serif;padding:24px 0;">
    <tr>
      <td align="center">
        <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background-color:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
          <!-- Header -->
          <tr>
            <td align="center" style="background:linear-gradient(135deg,#1a365d 0%,#2c5282 100%);background-color:#1a365d;padding:28px 24px 22px;color:#ffffff;text-align:center;">
              <img src="${logoUrl}" alt="${this.productName}" width="56" height="56" style="display:block;margin:0 auto 12px;border:0;outline:none;text-decoration:none;width:56px;height:56px;background-color:#ffffff;border-radius:12px;padding:6px;box-shadow:0 2px 6px rgba(0,0,0,0.15);" />
              <div style="font-size:22px;font-weight:700;letter-spacing:0.4px;text-align:center;">${this.productName}</div>
              <div style="font-size:12px;opacity:0.85;margin-top:4px;text-align:center;letter-spacing:0.5px;text-transform:uppercase;">Training Material Notification</div>
            </td>
          </tr>
          <!-- Status banner -->
          <tr>
            <td style="background-color:${colour.bg};color:${colour.text};padding:10px 24px;font-size:13px;font-weight:600;letter-spacing:0.2px;">
              ${this.escapeHtml(parts.statusLabel)}
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding:24px;color:#2d3748;font-size:14px;line-height:1.6;">
              <p style="margin:0 0 14px;">Hi <strong>${this.escapeHtml(parts.greetingName)}</strong>,</p>
              <p style="margin:0 0 18px;">${this.escapeHtml(parts.intro)}</p>
              ${requesterBanner}
              ${detailsHeader}
              ${detailsBlock}
              ${reasonBlock}
              <p style="margin:0;color:#4a5568;">${this.escapeHtml(parts.outro)}</p>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="background-color:#f7fafc;padding:16px 24px;border-top:1px solid #e2e8f0;color:#718096;font-size:12px;line-height:1.5;">
              <div style="color:#4a5568;font-weight:500;margin-bottom:2px;">Thanks,</div>
              <div style="color:#4a5568;font-weight:500;">${this.productName} Team</div>
              <div style="margin-top:10px;color:#a0aec0;font-size:11px;">This is an automated notification — please do not reply to this email.</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ── Transport ─────────────────────────────────────────────────────────
  // POST {emailServiceBaseUrl}/api/emails/send. Base URL and app_id come
  // from ui-config.json so deployments can swap email backends without a
  // rebuild. Failures are logged but never thrown — UI flows are
  // fire-and-forget; a missed email shouldn't roll back a successful approve
  // / cancel / submit action the user just took.
  private sendEmail(processName: string, payload: EmailPayload): Observable<boolean> {
    const baseUrl = (UserData.email_service_base_url || '').trim();
    const appId = (UserData.email_service_app_id || '').trim();

    if (!baseUrl || !appId) {
      console.warn(
        `[EmailNotificationService] [${processName}] missing EMAIL_SERVICE_BASE_URL or EMAIL_SERVICE_APP_ID in ui-config.json — skipping send`,
        { to: payload.to, subject: payload.subject }
      );
      return of(false);
    }
    if (!payload.to) {
      console.warn(`[EmailNotificationService] [${processName}] empty recipient — skipping send`, { subject: payload.subject });
      return of(false);
    }

    const url = `${baseUrl.replace(/\/+$/, '')}/api/emails/send`;

    // Backend ships emails AES-GCM-encrypted. Decrypt the recipient and the
    // user_id (current user's email, also encrypted at /userDetails) right
    // before POSTing so the email service receives plaintext addresses. The
    // fallback "shivesh@zibal.in" is plaintext and passes through unchanged
    // because isProbablyEncrypted rejects it.
    const decrypt$ = from(Promise.all([
      this.maybeDecrypt(payload.to),
      this.maybeDecrypt(getCurrentUserEmail())
    ]));

    return decrypt$.pipe(
      switchMap(([toDecrypted, userIdDecrypted]) => {
        const correlationId = this.generateCorrelationId();
        const body: EmailServiceRequest = {
          app_id: appId,
          to: [toDecrypted],
          subject: payload.subject,
          body: payload.body,
          body_html: payload.bodyHtml,
          correlation_id: correlationId,
          user_id: userIdDecrypted
        };
        console.log(`[EmailNotificationService] [${processName}] POST ${url}`, {
          correlationId,
          to: toDecrypted,
          user_id: userIdDecrypted,
          subject: payload.subject
        });
        const startedAt = Date.now();
        return this.http.post(url, body).pipe(
          map(() => {
            console.log(
              `[EmailNotificationService] [${processName}] send OK in ${Date.now() - startedAt}ms`,
              { correlationId, to: toDecrypted, subject: payload.subject }
            );
            return true;
          }),
          catchError(err => {
            console.error(
              `[EmailNotificationService] [${processName}] send FAILED in ${Date.now() - startedAt}ms`,
              { correlationId, url, to: toDecrypted, subject: payload.subject, err }
            );
            return of(false);
          })
        );
      })
    );
  }

  private async maybeDecrypt(value: string): Promise<string> {
    const v = (value || '').trim();
    if (!v) return v;
    if (!this.crypto.isProbablyEncrypted(v)) return v;
    try {
      return await this.crypto.decryptData(v);
    } catch (e) {
      console.error('[EmailNotificationService] decrypt failed; falling back to original', { value: v, e });
      return v;
    }
  }

  private generateCorrelationId(): string {
    // Lightweight non-cryptographic UUID-ish id; only used as a tracing tag
    // in the email service logs, so cryptographic randomness isn't required.
    return `tm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
